/**
 * User behavior analytics tracking
 */

import { logger } from '../logger';

interface BehaviorEvent {
  type: string;
  properties: Record<string, any>;
  timestamp: number;
}

interface UserSession {
  sessionId: string;
  startTime: number;
  lastActivity: number;
  pageViews: number;
  events: BehaviorEvent[];
}

class UserBehaviorTracker {
  private session: UserSession;
  private readonly SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  private readonly MAX_EVENTS_PER_SESSION = 100;

  constructor() {
    this.session = this.initSession();
    this.setupListeners();
  }

  /**
   * Initialize or restore session
   */
  private initSession(): UserSession {
    const stored = sessionStorage.getItem('user_session');
    
    if (stored) {
      try {
        const session = JSON.parse(stored);
        const isExpired = Date.now() - session.lastActivity > this.SESSION_TIMEOUT;
        
        if (!isExpired) {
          return session;
        }
      } catch (error) {
        logger.error('UserBehavior:initSession', error);
      }
    }

    return {
      sessionId: this.generateSessionId(),
      startTime: Date.now(),
      lastActivity: Date.now(),
      pageViews: 0,
      events: [],
    };
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Save session to storage
   */
  private saveSession() {
    try {
      sessionStorage.setItem('user_session', JSON.stringify(this.session));
    } catch (error) {
      logger.error('UserBehavior:saveSession', error);
    }
  }

  /**
   * Setup event listeners
   */
  private setupListeners() {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.track('page_hidden');
      } else {
        this.track('page_visible');
      }
    });

    // Track page unload
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });
  }

  /**
   * Track an event
   */
  track(eventType: string, properties: Record<string, any> = {}) {
    const event: BehaviorEvent = {
      type: eventType,
      properties,
      timestamp: Date.now(),
    };

    this.session.events.push(event);
    this.session.lastActivity = Date.now();

    // Keep only recent events
    if (this.session.events.length > this.MAX_EVENTS_PER_SESSION) {
      this.session.events.shift();
    }

    this.saveSession();

    if (import.meta.env.DEV) {
      logger.info('UserBehavior', `Event tracked: ${eventType}`, properties);
    }
  }

  /**
   * Track page view
   */
  trackPageView(path: string) {
    this.session.pageViews++;
    this.track('page_view', { path });
  }

  /**
   * Track button/link click
   */
  trackClick(element: string, properties: Record<string, any> = {}) {
    this.track('click', { element, ...properties });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(feature: string, properties: Record<string, any> = {}) {
    this.track('feature_usage', { feature, ...properties });
  }

  /**
   * Track form interaction
   */
  trackFormInteraction(formName: string, action: 'start' | 'complete' | 'abandon') {
    this.track('form_interaction', { formName, action });
  }

  /**
   * Track time spent on page
   */
  trackTimeOnPage(path: string, duration: number) {
    this.track('time_on_page', { path, duration });
  }

  /**
   * Get session metrics
   */
  getSessionMetrics() {
    const duration = Date.now() - this.session.startTime;
    const eventsByType = this.session.events.reduce((acc, event) => {
      acc[event.type] = (acc[event.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      sessionId: this.session.sessionId,
      duration,
      pageViews: this.session.pageViews,
      totalEvents: this.session.events.length,
      eventsByType,
      lastActivity: this.session.lastActivity,
    };
  }

  /**
   * End current session
   */
  endSession() {
    const metrics = this.getSessionMetrics();
    logger.info('UserBehavior', 'Session ended', metrics);
    
    // Could send metrics to analytics service here
    sessionStorage.removeItem('user_session');
  }

  /**
   * Get recent events
   */
  getRecentEvents(limit: number = 10): BehaviorEvent[] {
    return this.session.events.slice(-limit);
  }
}

export const userBehaviorTracker = new UserBehaviorTracker();
